import { demoInfo } from './demo';

/**
 * Ключи доступа (WebAuthn) в браузере: параметры от сервера приходят в JSON (двоичное — base64url),
 * ответ ключа уходит обратно тоже в JSON. Поля переводим явно — так в браузер уходит только то, что мы знаем.
 */

type Json = Record<string, unknown>;

export function passkeysSupported(): boolean {
  if (demoInfo() !== null) return true;
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function' && 'credentials' in navigator;
}

/** Человек закрыл окно ключа или время вышло — это не ошибка, сообщение не нужно. */
export function isPasskeyCancel(error: unknown): boolean {
  return error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError');
}

function fromB64(value: unknown): ArrayBuffer {
  const base = String(value).replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base + '='.repeat((4 - (base.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function toB64(buffer: ArrayBuffer): string {
  let binary = '';
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function obj(value: unknown): Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? Object.fromEntries(Object.entries(value)) : {};
}

function list(value: unknown): Json[] {
  return Array.isArray(value) ? value.map(obj) : [];
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return allowed.find((a) => a === value);
}

const VERIFICATION = ['required', 'preferred', 'discouraged'] as const;
const RESIDENT = ['required', 'preferred', 'discouraged'] as const;

function descriptors(value: unknown): PublicKeyCredentialDescriptor[] {
  return list(value).map((d) => ({ type: 'public-key', id: fromB64(d.id) }));
}

/** Время на ответ: сервер шлёт две минуты; если нет — пусть решает браузер. */
function timeout(value: unknown): { timeout?: number } {
  return typeof value === 'number' ? { timeout: value } : {};
}

function creationOptions(json: Json): PublicKeyCredentialCreationOptions {
  const rp = obj(json.rp);
  const user = obj(json.user);
  const selection = obj(json.authenticatorSelection);
  return {
    challenge: fromB64(json.challenge),
    rp: { id: String(rp.id), name: String(rp.name) },
    user: { id: fromB64(user.id), name: String(user.name), displayName: String(user.displayName ?? user.name) },
    pubKeyCredParams: list(json.pubKeyCredParams).map((p) => ({ type: 'public-key', alg: Number(p.alg) })),
    ...timeout(json.timeout),
    excludeCredentials: descriptors(json.excludeCredentials),
    authenticatorSelection: { residentKey: oneOf(selection.residentKey, RESIDENT) ?? 'required', requireResidentKey: true, userVerification: oneOf(selection.userVerification, VERIFICATION) ?? 'required' },
    attestation: 'none',
  };
}

function requestOptions(json: Json): PublicKeyCredentialRequestOptions {
  return {
    challenge: fromB64(json.challenge),
    ...(typeof json.rpId === 'string' ? { rpId: json.rpId } : {}),
    allowCredentials: descriptors(json.allowCredentials),
    userVerification: oneOf(json.userVerification, VERIFICATION) ?? 'preferred',
    ...timeout(json.timeout),
  };
}

function credentialJson(credential: PublicKeyCredential): Json {
  const r = credential.response;
  const response: Json = { clientDataJSON: toB64(r.clientDataJSON) };
  if (r instanceof AuthenticatorAttestationResponse) {
    response.attestationObject = toB64(r.attestationObject);
    response.transports = typeof r.getTransports === 'function' ? r.getTransports() : [];
  } else if (r instanceof AuthenticatorAssertionResponse) {
    response.authenticatorData = toB64(r.authenticatorData);
    response.signature = toB64(r.signature);
    if (r.userHandle !== null) response.userHandle = toB64(r.userHandle);
  }
  return { id: credential.id, rawId: toB64(credential.rawId), type: credential.type, response, clientExtensionResults: {} };
}

/** Создать ключ: устройство спросит отпечаток, лицо или ПИН и сохранит ключ (в iCloud, Google, Пассворке…). */
export async function createPasskey(options: Json): Promise<Json> {
  // Демо без сервера: подписи нет, «устройство» отвечает сразу.
  if (demoInfo() !== null) return { id: crypto.randomUUID(), type: 'public-key', response: {} };
  const credential = await navigator.credentials.create({ publicKey: creationOptions(options) });
  if (!(credential instanceof PublicKeyCredential)) throw new DOMException('no credential', 'NotAllowedError');
  return credentialJson(credential);
}

/** Подписать вызов сервера ключом: при входе без пароля устройство само предложит подходящий ключ. */
export async function signWithPasskey(options: Json): Promise<Json> {
  if (demoInfo() !== null) {
    const first = list(options.allowCredentials)[0];
    return { id: first === undefined ? 'none' : String(first.id), type: 'public-key', response: {} };
  }
  // Если окно ключа не ответило (например, приложение ушло в браузер) — через 2 минуты кнопка оживает, а не крутится вечно.
  const credential = await Promise.race([
    navigator.credentials.get({ publicKey: requestOptions(options) }),
    new Promise<never>((_, fail) => {
      window.setTimeout(() => { fail(new DOMException('timeout', 'NotAllowedError')); }, 120_000);
    }),
  ]);
  if (!(credential instanceof PublicKeyCredential)) throw new DOMException('no credential', 'NotAllowedError');
  return credentialJson(credential);
}
